import { useMeasure } from "@uidotdev/usehooks";
import PropTypes from "prop-types";
import { cloneElement, useEffect, useRef, useState } from "react";

const ROW_HEIGHT = 40;
const HEADER_ROW_HEIGHT = 56;

function isRowMultiLined(isHeader, element) {
  const { height } = element.getBoundingClientRect();

  if (isHeader) {
    return height > HEADER_ROW_HEIGHT;
  } else {
    return height > ROW_HEIGHT;
  }
}

export default function Row({
  children,
  className,
  header = false,
  id,
  onClick,
  selected,
}) {
  const [childrenToRender, setChildrenToRender] = useState(children);

  const [resizeRef, { height }] = useMeasure();
  const rowRef = useRef();

  // Control alignment of cells, when one of them is multilined
  // then all should be aligned top.
  const updateChildren = () => {
    let newChildren = children;

    if (isRowMultiLined(header, rowRef.current)) {
      newChildren = children.map((childElement, index) => {
        // User may force alignment on specific Cells, and so we respect that decision.
        if (childElement.props.align) {
          return childElement;
        } else {
          return cloneElement(childElement, { align: "top", key: index });
        }
      });
    }

    setChildrenToRender(newChildren);
  };

  useEffect(() => setChildrenToRender(children), [children])

  /** 
   * If needed in the future, we can un-comment this to adjust 
   * the default behavior. With this line enabled, the Cell contents
   * will align to top by default if the row is multi-lined.
   */
  // useEffect(updateChildren, [children, height]);

  const classNames = [header ? "lds-table-headerRow" : "lds-table-row"];

  if (selected) {
    classNames.push("selected");
  }

  if (onClick) {
    classNames.push("cursor-pointer");
  }

  classNames.push(className);

  return (
    <tr
      className={classNames.join(" ")}
      id={id}
      data-testid={id}
      onClick={onClick}
      role="row"
      ref={(el) => {
        // Attach element to multiple refs
        rowRef.current = el;
        resizeRef(el);
      }}
    >
      {childrenToRender}
    </tr>
  );
}

Row.propTypes = {
  className: PropTypes.string,
  children: PropTypes.any,
  id: PropTypes.string,
  header: PropTypes.bool,
  onClick: PropTypes.func,
  selected: PropTypes.bool,
};
